import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Input, Modal, Space, Tag, message } from 'antd';
import { schoolAdminAPI } from '../../services/api';

// The parent keys this panel by school so form values and requests cannot cross schools.
export default function SchoolJoinSettings({ schoolId, disabled }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();
  const alive = useRef(false), version = useRef(0), mutation = useRef(false), confirmation = useRef(null);
  const load = useCallback(async () => {
    const ticket = ++version.current;
    setLoading(true); setError(''); setState(null);
    try {
      const response = await schoolAdminAPI.getJoinPassword({ schoolId });
      if (!response.data?.success || String(response.data.data?.schoolId) !== String(schoolId)) throw new Error('无法读取学校加入口令状态');
      if (alive.current && ticket === version.current) setState(response.data.data);
    } catch (failure) {
      if (alive.current && ticket === version.current) setError(failure.response?.data?.message || '读取学校加入口令状态失败，请重试');
    } finally { if (alive.current && ticket === version.current) setLoading(false); }
  }, [schoolId]);
  useEffect(() => {
    alive.current = true;
    load();
    return () => { alive.current = false; version.current += 1; confirmation.current?.destroy(); };
  }, [load]);

  const save = async ({ password }) => {
    if (!alive.current || disabled || loading || mutation.current) return;
    mutation.current = true; setSaving(true);
    try {
      const response = await schoolAdminAPI.setJoinPassword({ schoolId, password: password.trim() });
      if (!response.data?.success) throw new Error('设置失败');
      if (!alive.current) return;
      form.resetFields(); setEditing(false); message.success('学校加入口令已保存');
      await load();
    } catch (failure) { if (alive.current) message.error(failure.response?.data?.message || '设置学校加入口令失败，请重试'); }
    finally { mutation.current = false; if (alive.current) setSaving(false); }
  };

  const stop = () => {
    if (!state?.passwordConfigured || disabled || loading || mutation.current) return;
    confirmation.current = modal.confirm({ title: '停用学校加入口令？', okText: '确认停用', cancelText: '取消', okButtonProps: { danger: true },
      content: '已有成员不受影响。有学校管理员时，新教师暂不能通过学校口令加入；重新设置口令后可恢复。',
      onOk: async () => {
        if (!alive.current || mutation.current) return;
        mutation.current = true; setSaving(true);
        try {
          const response = await schoolAdminAPI.disableJoinPassword({ schoolId });
          if (!response.data?.success) throw new Error('停用失败');
          if (!alive.current) return;
          message.success('学校加入口令已停用'); await load();
        } catch (failure) { if (alive.current) message.error(failure.response?.data?.message || '停用失败，请重试'); throw failure; }
        finally { mutation.current = false; if (alive.current) setSaving(false); }
      }
    });
  };

  return <>
    <div className="sw-inline-actions"><div><strong>学校加入口令</strong>
      <p>用于教师加入学校，与班级登录口令分别管理。</p>
      <div style={{ marginTop: 10 }}>{loading ? <Tag>读取中</Tag> : error ? <Tag>状态读取失败</Tag>
        : <Tag color={state?.passwordConfigured ? 'blue' : 'default'}>{state?.passwordConfigured ? '已设置' : '未设置'}</Tag>}</div>
    </div><Space wrap>
      <Button disabled={disabled || loading || saving || !state} onClick={() => { form.resetFields(); setEditing(true); }}>{state?.passwordConfigured ? '修改口令' : '设置口令'}</Button>
      {state?.passwordConfigured && <Button danger disabled={disabled || loading || saving} onClick={stop}>停用口令</Button>}
      <Button disabled={disabled || saving} loading={loading} onClick={load}>刷新状态</Button>
    </Space></div>
    {error ? <Alert type="error" showIcon message={error} /> : state && <Alert type={state.passwordConfigured ? 'info' : state.passwordRequired ? 'warning' : 'info'} showIcon
      message={state.passwordConfigured ? (state.passwordRequired ? '加入验证已启用，已有成员不受影响。' : '口令已设置，学校开通管理员后启用校验。')
        : state.passwordRequired ? '尚未设置口令，新教师暂不能通过学校口令加入。' : '可先设置口令，学校开通管理员后启用校验。'} />}
    <Modal title={state?.passwordConfigured ? '修改学校加入口令' : '设置学校加入口令'} open={editing} forceRender
      okText="保存口令" cancelText="取消" confirmLoading={saving} onOk={() => form.submit()}
      onCancel={() => { if (!saving) { setEditing(false); form.resetFields(); } }}>
      <Form form={form} layout="vertical" onFinish={save}>
        <Form.Item name="password" label="新口令" rules={[{ required: true, message: '请输入口令' },
          { pattern: /^[A-Za-z0-9]{4,20}$/, message: '请输入4至20位字母或数字' }]}>
          <Input.Password autoComplete="new-password" maxLength={20} placeholder="4至20位字母或数字" />
        </Form.Item>
        <Form.Item name="confirmation" label="再次输入口令" dependencies={['password']} rules={[{ required: true, message: '请再次输入口令' },
          ({ getFieldValue }) => ({ validator: (_, value) => value === getFieldValue('password') ? Promise.resolve() : Promise.reject(new Error('两次输入的口令不一致')) })]}>
          <Input.Password autoComplete="new-password" maxLength={20} />
        </Form.Item>
      </Form>
    </Modal>
    {contextHolder}
  </>;
}
